import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Salary } from '@tink/core/src/components/atoms/Icons/Salary';
import { Spacer } from '@tink/core/src/components/atoms/Spacer/Spacer';
import { Profile } from '@tink/core/src/components/atoms/Icons/Profile';
import { FourStoryBuilding } from '@tink/core/src/components/atoms/Icons/FourStoryBuilding';
import { CircledIcon } from '../../../components/atoms/Icons/CircledIcon';
import { Header } from '../../../components/organisms/Header/Header';
import { H1 } from '../../../components/atoms/Heading/Heading';
import { ProviderViewSet } from '../../../utilities/provider-view-set';
import { ScreenWrapper } from '../../../components/atoms/ScreenWrapper/ScreenWrapper';
import { SelectableOption } from '../../../components/screens/molecules/SelectableOption/SelectableOption';
import { Subtitle } from '../../../components/atoms/Subtitle/Subtitle';
import { AuthenticationUserType, ProviderEntity } from '../../../models/provider';
import { ContentBody } from '../../../components/atoms/ContentBody/ContentBody';
import { PageView } from '../../../models/analytics';

import { useVersion } from '../../../components/ApplicationProviders/VersionProvider/VersionProvider';
import { messages } from './messages';

export type AuthenticationTypePickerProps = {
  onBack: () => void;
  providers: ProviderEntity[];
  financialInstitutionId: string;
  onProviderItemClick: ({
    authenticationUserType,
  }: {
    authenticationUserType: AuthenticationUserType;
  }) => void;
};

export const AuthenticationTypePicker = ({
  onBack,
  onProviderItemClick,
  financialInstitutionId,
  providers: allProviders,
}: AuthenticationTypePickerProps) => {
  const intl = useIntl();
  const {
    version: { version },
  } = useVersion();

  const providers = allProviders.filter((provider) => {
    return provider.financialInstitutionId === financialInstitutionId;
  });

  const authenticationUserTypeGroups = new ProviderViewSet(
    providers,
    version
  ).groupByAuthenticationUserType();

  const personalGroup = authenticationUserTypeGroups.find(
    (group) => group.title === AuthenticationUserType.PERSONAL
  );
  const businessGroup = authenticationUserTypeGroups.find(
    (group) => group.title === AuthenticationUserType.BUSINESS
  );
  const corporateGroup = authenticationUserTypeGroups.find(
    (group) => group.title === AuthenticationUserType.CORPORATE
  );

  return (
    <ScreenWrapper>
      <Header onBack={onBack} pageView={PageView.AUTHENTICATION_TYPE_SELECTION_SCREEN} />
      <ContentBody>
        <H1>
          <FormattedMessage {...messages.loginType} />
        </H1>
        <Subtitle>
          <FormattedMessage {...messages.whichWayToLogin} />
        </Subtitle>
        <Spacer size="normal" />
        {personalGroup && (
          <SelectableOption
            Icon={<CircledIcon Icon={Profile} />}
            text={intl.formatMessage(messages.personal)}
            onClick={() => {
              onProviderItemClick({ authenticationUserType: AuthenticationUserType.PERSONAL });
            }}
          />
        )}
        {businessGroup && (
          <SelectableOption
            Icon={<CircledIcon Icon={Salary} />}
            text={intl.formatMessage(messages.business)}
            onClick={() => {
              onProviderItemClick({ authenticationUserType: AuthenticationUserType.BUSINESS });
            }}
          />
        )}
        {corporateGroup && (
          <SelectableOption
            Icon={<CircledIcon Icon={FourStoryBuilding} />}
            text={intl.formatMessage(messages.corporate)}
            onClick={() => {
              onProviderItemClick({ authenticationUserType: AuthenticationUserType.CORPORATE });
            }}
          />
        )}
      </ContentBody>
    </ScreenWrapper>
  );
};
